import { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// onFilesSelected يستلم مصفوفة File[] قبل الرفع (للاقتصاص)
const UploadArea = ({ onFilesSelected, maxCount = 8, currentCount = 0, disabled = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const atLimit = disabled || (typeof maxCount === 'number' && currentCount >= maxCount);

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (atLimit) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    if (atLimit) return;
    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    if (atLimit) return;
    const files = Array.from(e?.target?.files || []);
    handleFiles(files);
    // reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleFiles = (files) => {
    const validFiles = files.filter((file) => {
      const isImage = file?.type?.startsWith('image/');
      const isValidSize = file?.size <= 10 * 1024 * 1024;
      return isImage && isValidSize;
    });

    if (validFiles.length === 0) {
      alert('يرجى اختيار صور صالحة (أقل من 10 ميجابايت)');
      return;
    }

    onFilesSelected(validFiles);
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-warm mb-4 md:mb-6">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 md:p-8 lg:p-12 text-center transition-smooth ${
          atLimit
            ? 'border-muted bg-muted/30 cursor-not-allowed'
            : isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={atLimit}
        />

        <div className="flex flex-col items-center gap-3 md:gap-4">
          <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Upload" size={24} color="var(--color-primary)" className="md:w-8 md:h-8 lg:w-10 lg:h-10" />
          </div>

          {atLimit ? (
            <div className="text-center">
              <p className="text-base md:text-lg font-semibold text-foreground mb-1">
                الحد الأقصى لصور المعرض ({maxCount} صور)
              </p>
              <p className="text-sm text-muted-foreground">
                وصلت إلى الحد الأقصى ({currentCount}/{maxCount}). احذف صورةً لرفع غيرها.
              </p>
            </div>
          ) : (
            <>
              <div>
                <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-2">
                  اسحب الصور وأفلتها هنا
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-4">
                  أو انقر لتحديد الملفات من جهازك
                </p>
              </div>

              <Button
                variant="default"
                iconName="Image"
                iconPosition="right"
                onClick={() => fileInputRef?.current?.click()}
              >
                اختر الصور
              </Button>

              <div className="text-xs md:text-sm text-muted-foreground mt-4 space-y-1">
                <p>الحد الأقصى: {maxCount} صور ({currentCount}/{maxCount})</p>
                <p>الحد الأقصى لحجم الملف: 10 ميجابايت</p>
                <p>الصيغ المدعومة: JPG, PNG, WEBP</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadArea;
